import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendUp, CheckCircle, Lightning, ArrowRight, Target, ChartLineUp,
  CircleNotch, Trophy, Clock, Sparkle,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import IndexStrip from "../components/IndexStrip";
import Seo from "../components/Seo";
import TelegramCTA from "../components/TelegramCTA";

function StatTile({ icon: Icon, label, value, accent = "text-primary-fg", testid }) {
  return (
    <div className="border border-subtle rounded-sm p-5 bg-surface/60 backdrop-blur-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className={`text-3xl font-mono tabular-nums mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

function WinCard({ win, index }) {
  const isLong = win.signal_type === "long_term";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.6) }}
      className="border border-subtle rounded-sm bg-surface/80 backdrop-blur-sm p-5 hover:border-pos transition-colors"
      data-testid={`win-card-${win.symbol}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-mono text-lg font-semibold text-primary-fg tracking-wide">{win.symbol}</div>
          {win.company_name && (
            <div className="text-[11px] text-muted-fg truncate max-w-[180px]" title={win.company_name}>
              {win.company_name}
            </div>
          )}
        </div>
        <span
          className={
            "px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest border " +
            (isLong ? "border-mod-soft bg-mod-soft text-mod" : "border-pos-soft bg-pos-soft text-pos")
          }
        >
          {isLong ? "Long Term" : "Short Term"}
        </span>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-mono tabular-nums text-pos">+{win.return_pct.toFixed(1)}%</span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-pos flex items-center gap-1">
          <CheckCircle size={11} weight="fill" /> Target Hit
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs font-mono">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-fg">Entry</div>
          <div className="text-primary-fg tabular-nums">₹{win.entry.toLocaleString("en-IN")}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-fg">Exit</div>
          <div className="text-pos tabular-nums">₹{win.exit.toLocaleString("en-IN")}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-muted-fg">Held</div>
          <div className="text-secondary-fg tabular-nums flex items-center gap-1">
            <Clock size={10} /> {win.days_held}d
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Wins() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/signals/top-performers?days=${windowDays}&limit=24`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch(() => { if (!cancelled) setData({ items: [], total_wins: 0, total_losses: 0, win_rate_pct: 0, avg_return_pct: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  const items = data?.items || [];
  const winRate = data?.win_rate_pct ?? "—";
  const totalWins = data?.total_wins ?? "—";
  const avg = data?.avg_return_pct ?? "—";

  return (
    <div className="min-h-screen bg-app grain relative overflow-hidden" data-testid="wins-page">
      <Seo
        title="Track Record · Real wins, public ledger — Breakout Scanner"
        description={`${totalWins} winning breakout signals · ${winRate}% win rate · +${avg}% avg return over the last ${windowDays} days. Multi-year NSE breakout signals with precise Entry / Stop-Loss / Target.`}
        path="/wins"
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          color: "hsl(var(--text-primary))",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, hsl(160 84% 39%), transparent)" }}
      />

      <header className="relative z-10 px-6 lg:px-12 py-5 border-b border-subtle bg-app/60 backdrop-blur-sm flex items-center justify-between flex-wrap gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="wins-home">
          <Logo size={44} />
          <div className="flex items-baseline gap-1.5 font-mono text-base tracking-[0.22em] uppercase">
            <span className="text-primary-fg font-semibold">Breakout</span>
            <span className="text-pos font-semibold">Scanner</span>
          </div>
        </Link>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
          <Link to="/login" className="text-secondary-fg hover:text-primary-fg" data-testid="wins-login">Sign in</Link>
          <span className="text-primary-fg/30">|</span>
          <Link
            to="/register"
            data-testid="wins-cta-signup"
            className="px-3 py-1.5 rounded-sm flex items-center gap-1.5"
            style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
          >
            Start free <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </header>

      <IndexStrip />

      <section className="relative z-10 px-6 lg:px-12 py-10 lg:py-14 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-pos-soft bg-pos-soft text-pos font-mono text-[10px] uppercase tracking-[0.25em] mb-5">
            <Sparkle size={10} weight="fill" /> Live track record · last {windowDays} days
          </div>
          <h1 className="font-medium leading-[0.95] tracking-tight text-primary-fg text-[44px] sm:text-[64px] lg:text-[76px] max-w-4xl">
            Real wins<span className="text-pos">.</span>
            <br />
            Public ledger<span className="text-pos">.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-secondary-fg text-base lg:text-lg leading-relaxed">
            Every breakout signal we publish is tracked end-to-end — Entry, Stop-Loss, Target.
            Below is an anonymised list of the <span className="text-primary-fg">top-performing breakouts</span> that
            hit their target in the last {windowDays} days.
          </p>
        </motion.div>

        {/* Window selector */}
        <div className="mt-8 flex items-center gap-2 flex-wrap">
          {[7, 30, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              data-testid={`wins-window-${d}`}
              className={
                "px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                (windowDays === d
                  ? "bg-pos-soft border-pos-soft text-pos"
                  : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
              }
            >
              {d} days
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            icon={Trophy}
            label="Wins"
            value={data ? data.total_wins : "—"}
            accent="text-pos"
            testid="stat-total-wins"
          />
          <StatTile
            icon={Target}
            label="Win rate"
            value={data ? `${data.win_rate_pct}%` : "—"}
            accent="text-primary-fg"
            testid="stat-win-rate"
          />
          <StatTile
            icon={ChartLineUp}
            label="Avg return"
            value={data ? `+${data.avg_return_pct}%` : "—"}
            accent="text-pos"
            testid="stat-avg-return"
          />
          <StatTile
            icon={Lightning}
            label="Best return"
            value={items.length ? `+${items[0].return_pct.toFixed(1)}%` : "—"}
            accent="text-pos"
            testid="stat-best-return"
          />
        </div>

        {/* Wins grid */}
        <div className="mt-10">
          {loading ? (
            <div className="flex justify-center py-20">
              <CircleNotch className="animate-spin text-mod" size={24} weight="bold" />
            </div>
          ) : items.length === 0 ? (
            <div className="border border-subtle rounded-sm bg-surface p-10 text-center" data-testid="wins-empty">
              <Trophy size={28} className="mx-auto text-muted-fg mb-3" />
              <div className="text-primary-fg font-medium">No closed breakouts in this window yet</div>
              <div className="text-sm text-muted-fg mt-1 max-w-md mx-auto">
                As live signals hit Target, they'll be tracked here automatically. Try a longer window or check back soon.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wins-grid">
              {items.map((w, idx) => <WinCard key={`${w.symbol}-${w.closed_at || idx}`} win={w} index={idx} />)}
            </div>
          )}
        </div>

        {/* CTA section */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 border border-pos-soft rounded-sm bg-pos-soft/50 backdrop-blur-sm p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10"
        >
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-pos mb-2">/// Don't miss the next one</div>
            <h2 className="text-2xl lg:text-3xl text-primary-fg font-medium tracking-tight">
              See <span className="text-pos">live breakouts</span> the moment they trigger
            </h2>
            <p className="mt-2 text-secondary-fg max-w-2xl">
              Every signal includes precise Entry, Stop-Loss and Target — with AI fundamentals + volume confirmation.
              Free trial covers 1 long-term + 3 short-term picks instantly.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
            <Link
              to="/register"
              data-testid="wins-cta-trial"
              className="px-6 py-3 rounded-sm font-medium flex items-center justify-center gap-2 hover:opacity-90"
              style={{ background: "hsl(var(--signal-positive))", color: "#fff" }}
            >
              <Lightning size={14} weight="fill" /> Start free
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-3 rounded-sm border border-subtle bg-app text-primary-fg hover:bg-surface-hover flex items-center justify-center gap-2"
              data-testid="wins-cta-pricing"
            >
              ₹5 trial · 5 days <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>

        <div className="mt-8">
          <TelegramCTA variant="banner" testId="wins-telegram-cta" />
        </div>

        {/* SEBI compliance banner */}
        <div
          className="mt-6 px-4 py-3 border border-mod/40 bg-mod-soft/30 rounded-sm text-[11px] font-mono leading-relaxed text-mod"
          data-testid="wins-sebi-disclaimer"
        >
          <div className="font-bold uppercase tracking-widest mb-1">⚠️ Important disclosure</div>
          <span className="normal-case text-secondary-fg">
            Breakout Scanner is an <b>educational/research tool</b>. We are{" "}
            <b className="text-mod">NOT a SEBI-registered Research Analyst</b> or
            Investment Advisor. Past wins do not guarantee future returns. All
            signals are technical observations, not investment recommendations.
            Trading involves substantial capital risk. Consult your
            SEBI-registered financial advisor before any trade.
          </span>
        </div>

        <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-wrap gap-3 items-center">
          <span><TrendUp size={10} className="inline mr-1" /> All breakouts closed at Target — verified</span>
          <span className="text-primary-fg/30">|</span>
          <span>Educational research · Not SEBI-registered</span>
        </div>
      </section>

      <footer className="relative z-10 px-6 lg:px-12 py-5 border-t border-subtle text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-wrap items-center gap-4">
        <span>© Breakout Scanner</span>
        <Link to="/terms" className="hover:text-primary-fg">Terms</Link>
        <Link to="/refund" className="hover:text-primary-fg">Refund</Link>
        <Link to="/privacy" className="hover:text-primary-fg">Privacy</Link>
        <Link to="/about" className="hover:text-primary-fg">About</Link>
        <span className="ml-auto">
          <Link to="/login" className="hover:text-primary-fg">Sign in →</Link>
        </span>
      </footer>
    </div>
  );
}
